import { useState } from "react";
import { useAuth } from "../lib/AuthContext";
import { apiPost } from "../lib/api";
import { COLORS } from "../lib/colors";

const API_URL = import.meta.env.VITE_API_URL || "https://api.careersorter.co";

const DEGREE_OPTIONS = ["Any", "Bachelor's", "Master's", "PhD"];
const YEAR_OPTIONS = ["Any", 2025, 2026, 2027, 2028];
const LANGUAGE_OPTIONS = ["English", "Finnish", "Swedish", "German", "French", "Spanish", "Chinese", "Russian"];

export default function SearchTab() {
  const { user, session, schoolId } = useAuth();
  const [degreeFilter, setDegreeFilter] = useState("Any");
  const [graduationYear, setGraduationYear] = useState("Any");
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [skillsFilter, setSkillsFilter] = useState("");
  const [jdText, setJdText] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const toggleLanguage = (lang) => {
    setSelectedLanguages(prev =>
      prev.includes(lang) ? prev.filter(l => l !== lang) : [...prev, lang]
    );
  };

  const handleSearch = async () => {
    if (!jdText.trim()) {
      setError("Please paste a Job Description first.");
      return;
    }
    setError("");
    setSearching(true);
    setResults(null);

    const skillsList = skillsFilter.trim()
      ? skillsFilter.split(",").map(s => s.trim()).filter(Boolean)
      : null;

    const payload = {
      query: jdText,
      school_id: schoolId,
      skills: skillsList,
      languages: selectedLanguages.length ? selectedLanguages : null,
      min_experience: null,
      degree_level: degreeFilter === "Any" ? null : degreeFilter,
      graduation_year: graduationYear === "Any" ? null : graduationYear,
      limit: 10,
      recruiter_id: user.id,
      recruiter_email: user.email,
    };

    try {
      const data = await apiPost("/search", payload, session.access_token);
      setResults(data.results || []);
    } catch (err) {
      setError(`Search failed: ${err.message}`);
    } finally {
      setSearching(false);
    }
  };

  const selectStyle = {
    padding: "10px 14px",
    borderRadius: 8,
    border: `1px solid ${COLORS.border}`,
    fontSize: 14,
    fontFamily: "inherit",
    backgroundColor: COLORS.surface,
    color: COLORS.text,
    width: "100%",
    outline: "none",
  };

  const labelStyle = {
    display: "block",
    fontSize: 13,
    fontWeight: 600,
    color: COLORS.textSecondary,
    marginBottom: 6,
  };

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: COLORS.text, marginBottom: 4 }}>
        Search Talent Pool
      </h2>
      <p style={{ fontSize: 13, color: COLORS.textMuted, marginBottom: 24 }}>
        Find matching candidates using job description and filters
      </p>

      {/* Filters */}
      <div className="filters-grid" style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 20,
        marginBottom: 20,
      }}>
        <div>
          <label style={labelStyle}>Degree Level</label>
          <select value={degreeFilter} onChange={e => setDegreeFilter(e.target.value)} style={selectStyle}>
            {DEGREE_OPTIONS.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Graduation Year</label>
          <select value={graduationYear} onChange={e => setGraduationYear(e.target.value)} style={selectStyle}>
            {YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Languages</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {LANGUAGE_OPTIONS.map(lang => (
              <button
                key={lang}
                onClick={() => toggleLanguage(lang)}
                style={{
                  padding: "5px 12px",
                  borderRadius: 6,
                  border: `1px solid ${selectedLanguages.includes(lang) ? COLORS.accent : COLORS.border}`,
                  backgroundColor: selectedLanguages.includes(lang) ? COLORS.accentLight : COLORS.surface,
                  color: selectedLanguages.includes(lang) ? COLORS.accent : COLORS.textSecondary,
                  fontSize: 13,
                  fontWeight: 500,
                  fontFamily: "inherit",
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
              >
                {lang}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label style={labelStyle}>Skills (comma-separated)</label>
          <input
            type="text"
            value={skillsFilter}
            onChange={e => setSkillsFilter(e.target.value)}
            placeholder="e.g. Python, Excel, Project Management"
            style={selectStyle}
          />
        </div>
      </div>

      {/* JD Text Area */}
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>Job Description / Requirements</label>
        <textarea
          value={jdText}
          onChange={e => setJdText(e.target.value)}
          placeholder="Paste a job description or describe the ideal candidate..."
          style={{
            ...selectStyle,
            minHeight: 160,
            resize: "vertical",
          }}
        />
      </div>

      {/* Search Button */}
      <button
        onClick={handleSearch}
        disabled={searching}
        style={{
          padding: "11px 32px",
          borderRadius: 8,
          border: "none",
          backgroundColor: COLORS.accent,
          color: "#fff",
          fontSize: 14,
          fontWeight: 600,
          fontFamily: "inherit",
          cursor: searching ? "wait" : "pointer",
          opacity: searching ? 0.7 : 1,
          transition: "opacity 0.15s",
        }}
      >
        {searching ? "Searching..." : "Search Candidates"}
      </button>

      {/* Error */}
      {error && (
        <div style={{
          padding: "10px 14px", borderRadius: 8, marginTop: 16,
          backgroundColor: COLORS.redLight, color: COLORS.red, fontSize: 13,
        }}>{error}</div>
      )}

      {/* Results */}
      {results && (
        <div style={{ marginTop: 24 }}>
          {results.length === 0 ? (
            <div style={{
              padding: 20, borderRadius: 8,
              backgroundColor: COLORS.orangeLight, color: COLORS.orange, fontSize: 14,
              textAlign: "center",
            }}>
              No matching candidates found.
            </div>
          ) : (
            <>
              <p style={{ fontSize: 14, fontWeight: 600, color: COLORS.green, marginBottom: 16 }}>
                Found {results.length} matching candidates
              </p>
              {results.map(candidate => {
                const similarity = candidate.similarity || 0;
                const scorePct = `${(similarity * 100).toFixed(0)}%`;
                const isExpanded = expandedId === candidate.id;

                return (
                  <div key={candidate.id} style={{
                    backgroundColor: COLORS.surface,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: 10,
                    marginBottom: 10,
                    overflow: "hidden",
                  }}>
                    {/* Header (clickable) */}
                    <div
                      onClick={() => setExpandedId(isExpanded ? null : candidate.id)}
                      style={{
                        padding: "14px 18px",
                        cursor: "pointer",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <span style={{ fontSize: 15, fontWeight: 600, color: COLORS.text }}>
                          {candidate.name}
                        </span>
                        <span style={{
                          fontSize: 12, fontWeight: 600,
                          color: similarity >= 0.7 ? COLORS.green : similarity >= 0.5 ? COLORS.orange : COLORS.textMuted,
                          backgroundColor: similarity >= 0.7 ? COLORS.greenLight : similarity >= 0.5 ? COLORS.orangeLight : COLORS.surfaceAlt,
                          padding: "2px 8px", borderRadius: 4,
                        }}>
                          {scorePct} match
                        </span>
                      </div>
                      <span style={{ fontSize: 14, color: COLORS.textMuted }}>
                        {isExpanded ? "−" : "+"}
                      </span>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div style={{
                        padding: "0 18px 16px",
                        borderTop: `1px solid ${COLORS.borderLight}`,
                        paddingTop: 14,
                      }}>
                        {candidate.skills?.length > 0 && (
                          <p style={{ fontSize: 13, color: COLORS.textSecondary, marginBottom: 8 }}>
                            <strong>Skills:</strong> {candidate.skills.join(", ")}
                          </p>
                        )}
                        <p style={{ fontSize: 13, color: COLORS.textSecondary, marginBottom: 8 }}>
                          <strong>Experience:</strong> {candidate.total_experience_years ?? "N/A"} years
                        </p>
                        <p style={{ fontSize: 13, color: COLORS.textSecondary, marginBottom: 12 }}>
                          <strong>Summary:</strong> {candidate.cv_summary || "N/A"}
                        </p>
                        {candidate.pdf_url && candidate.pdf_url !== "storage_temporarily_disabled" && (
                          <a
                            href={`${API_URL}/view_cv/${candidate.id}?token=${encodeURIComponent(session?.access_token || "")}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: "inline-block",
                              padding: "8px 16px",
                              borderRadius: 6,
                              backgroundColor: COLORS.accentLight,
                              color: COLORS.accent,
                              fontSize: 13,
                              fontWeight: 600,
                              textDecoration: "none",
                            }}
                          >
                            View CV
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}
